import * as angular from 'angular';
import {IController} from 'angular';
import * as _ from 'lodash';
import {BatchRequestsService} from '../../../batch/services/batch-requests.service';
import {BatchDialogService} from '../../../batch/services/batch-dialogs-service.service';
import {BringozLoaderService} from '../../../commons/services/bringoz-loader.service';
import {ConfirmDialogService} from '../../../commons/services/confirm-dialog.service';
import {ConfirmChangeStateService} from '../../../commons/services/confirm-change-state.service';
import {PricePlanDialogService} from '../../services/price-plan-dialog-service.service';
import {PricePlanAlertsService} from '../../services/price-plan-alerts.service';
import {
  PROGRAM_IMPORT_TYPES,
  PROGRAM_SCHEMA_PERMISSIONS,
  ProgramImportColumn,
  ProgramImportType
} from '../../constants/program-import.const';

/**
 * Row as returned by /{importerPath}/validate (for grouped sections, one entry of a group with the
 * group's `accountId` / `programType` copied onto it). The server applies only `accountId`,
 * `programType` and `values` on import; `values` is null when the row has a blocking error.
 * `rawRow` holds what the user typed and feeds the table. `rowId`, `errorsDisplay`, `hasErrors`
 * and `cells` are view-only and stripped before import.
 */
export interface ProgramImportRow {
  rowNumber?: number;
  accountId?: string;
  programType?: string;
  values?: {[field: string]: any} | null;
  mappingErrors?: Array<ProgramImportMappingError | string>;
  rawRow?: any;
  // Rows removed together: one unit sharing the unit's `values` (sections with `unitRowsKey`), or
  // one account + program group (sections with `removeGroupAsWhole`).
  unitId?: string;
  rowId?: string;
  errorsDisplay?: string;
  hasErrors?: boolean;
  hasWarnings?: boolean;
  cells?: string[];
}

/**
 * One account + program of the "Week Preview" tab: the week the import leaves behind. `days` is
 * empty for groups that will not be imported (any row with an error skips the whole group).
 */
export interface ProgramImportWeekGroup {
  key: string;
  account: string;
  program: string;
  importable: boolean;
  days: Array<{day: string, windows: Array<{from: number, label: string, enabled: boolean}>}>;
}

const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export interface ProgramImportMappingError {
  type?: string;
  description?: string;
  message?: string;
  fieldName?: string;
}

/**
 * Import flow for Program section settings (PRD-1726): upload a file, review the server validation,
 * and import only when the whole file is clean, since the spec forbids partial imports.
 */
export class ProgramImportViewCtrl implements IController {

  importTypeId: string;

  public importTypes: ProgramImportType[] = PROGRAM_IMPORT_TYPES;
  public schemaPermissions: string[] = PROGRAM_SCHEMA_PERMISSIONS;
  public importType: ProgramImportType;
  public sectionOptionsWrapper: any;
  public rows: ProgramImportRow[] = [];
  public visibleRows: ProgramImportRow[] = [];
  public weekPreview: ProgramImportWeekGroup[] = [];
  public totals: {total: number, errors: number} = {total: 0, errors: 0};
  public currentNavItem: string = 'total';
  public uploadComplete: boolean = false;
  public canImport: boolean = false;
  public isFieldMappingOpen: boolean = false;
  public isLoading: boolean = false;
  public schemas: any[] = [];
  public schemaDefaultFields: any[] = [];

  /* @ngInject */
  constructor(
    private $state,
    private $log,
    private $q,
    private UUIDService,
    private pricePlanDialogService: PricePlanDialogService,
    private pricePlanAlertsService: PricePlanAlertsService,
    private batchRequestsService: BatchRequestsService,
    private batchDialogService: BatchDialogService,
    private bringozLoaderService: BringozLoaderService,
    private confirmDialogService: ConfirmDialogService,
    private confirmChangeStateService: ConfirmChangeStateService
  ) {
  }

  $onInit() {
    this.sectionOptionsWrapper = {
      list: _.map(this.importTypes, (type) => ({id: type.id, name: type.label})),
      propertyRef: 'name'
    };

    this._selectImportType(this.importTypeId);
  }

  onSelectSection(sectionId: string) {
    if (!sectionId || sectionId === this.importType?.id) {
      return;
    }

    this._selectImportType(sectionId);
  }

  changeNavItem(item: string) {
    this.currentNavItem = item;
    this._applyRows(this.rows);
  }

  onUpload(event: Event) {
    this.pricePlanDialogService
      .showUploadProgramSettingsDialog(event, this.importType, this.schemas)
      .then((rows) => {
        this.currentNavItem = 'total';
        this._applyRows(_.map(this._flattenGroups(rows || []), (row) => this._enrichRow(row)));
        this.confirmChangeStateService.setIsEnabled(this.uploadComplete);
      }, () => {
      });
  }

  onRemoveRow(row: ProgramImportRow) {
    // A unit (or a group the server replaces as a whole) is removed together.
    this._applyRows(_.reject(this.rows, (r) => row.unitId ? r.unitId === row.unitId : r.rowId === row.rowId));

    if (!this.rows.length) {
      this.currentNavItem = 'total';
      this.confirmChangeStateService.reset();
    }
  }

  onCancel(event: Event) {
    return this.confirmDialogService
      .confirm('Cancel Upload', 'Are you sure you want to cancel the upload?', event)
      .then(() => {
        this._reset();
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  onImport(event: Event) {
    // Never a partial import: the button is disabled while any row has an error.
    if (!this.canImport) {
      return;
    }

    if (!this.importType.importConfirm) {
      this._import(event);
      return;
    }

    this.confirmDialogService
      .confirm('Import', this.importType.importConfirm, event)
      .then(() => {
        this._import(event);
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  onBackToPrograms() {
    this.confirmChangeStateService.reset();
    this.$state.go('pricePlans', {}, {reload: true});
  }

  onOpenFieldMappingPanel() {
    this.isFieldMappingOpen = true;
  }

  onCloseFieldMappingPanel() {
    this.isFieldMappingOpen = false;
  }

  onSchemaCreated(schema) {
    this.schemas = [...this.schemas, schema];
  }

  onSchemaUpdated(schema) {
    this.schemas = _.map(this.schemas, (s) => s.id === schema.id ? schema : s);
  }

  onSchemaDeleted(schemaId) {
    this.schemas = _.reject(this.schemas, (s) => s.id === schemaId);
  }

  // ******************************
  //        Private methods
  // ******************************

  private _import(event: Event) {
    const items = this._toImportPayload(this.rows);

    this.bringozLoaderService.on();

    this.batchRequestsService.createAgentAssignmentsBatch(this.importType.importerPath, items)
      .then((batch) => {
        this.bringozLoaderService.off();
        return this.batchDialogService.batchProgramImportProgress(
          event, batch, this.importType.statusPath, this.importType.label, this.rows.length
        );
      })
      .then((errorItems: any) => {
        this._applyBatchErrors(errorItems || []);
      })
      .catch((error) => {
        if (error) {
          this.pricePlanAlertsService.importProgramSettingsError(error);
          this.$log.error('Failed to import program settings', error);
        } else {
          this.$log.info('Dialog Closed');
        }
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });
  }

  private _selectImportType(sectionId: string) {
    this.importType = _.find(this.importTypes, (type) => type.id === sectionId) || _.head(this.importTypes);
    this.isFieldMappingOpen = false;
    this._reset();
    this._loadSchemas();
  }

  private _loadSchemas() {
    this.isLoading = true;
    this.schemas = [];
    this.schemaDefaultFields = [];

    // batchRequestsService returns native promises - $q.all adopts them so the results
    // land inside a digest. A failure must not block uploading, it only means no saved
    // mapping can be picked, hence the per-call catch.
    this.$q.all([
      this.$q.when(this.batchRequestsService.getSchemas(this.importType.entity)).catch(() => []),
      this.$q.when(this.batchRequestsService.getSchemaDefaultFields(this.importType.entity)).catch(() => [])
    ])
      .then(([schemas, schemaDefaultFields]: any[]) => {
        this.schemas = schemas || [];
        this.schemaDefaultFields = schemaDefaultFields || [];
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private _applyRows(rows: ProgramImportRow[]) {
    this.rows = rows;
    this.totals = this._getTotals(rows);
    this.uploadComplete = !!rows.length;
    this.canImport = !!rows.length && !this.totals.errors;
    this.visibleRows = this.currentNavItem === 'errors'
      ? _.filter(rows, (row) => row.hasErrors)
      : rows;
    this.weekPreview = this.importType?.weekPreview ? this._buildWeekPreview(rows) : [];
  }

  /**
   * The Hours of Operation import replaces the whole week of each program, so weekdays missing
   * from the file end up empty. The preview makes that visible before anything is saved, which the
   * row table alone cannot (it only lists the windows that exist).
   */
  private _buildWeekPreview(rows: ProgramImportRow[]): ProgramImportWeekGroup[] {
    const groups = _.groupBy(rows, (row) =>
      `${row.accountId || 'typed:' + _.get(row.rawRow, 'accountId')}|${row.programType || 'typed:' + _.get(row.rawRow, 'programName')}`);

    return _.map(groups, (groupRows, key) => {
      const importable = !_.some(groupRows, (row) => row.hasErrors);
      const windowsByDay: {[day: string]: Array<{from: number, label: string, enabled: boolean}>} = {};

      if (importable) {
        _.forEach(groupRows, (row) => {
          _.forEach(_.get(row.values, 'days', []), (day: string) => {
            windowsByDay[day] = windowsByDay[day] || [];
            windowsByDay[day].push({
              from: row.values.from,
              label: `${this._formatTime(row.values.from)} - ${this._formatTime(row.values.to)}`,
              enabled: row.values.enabled
            });
          });
        });
      }

      return {
        key,
        account: _.get(_.head(groupRows).rawRow, 'accountId'),
        program: _.get(_.head(groupRows).rawRow, 'programName'),
        importable,
        days: importable
          ? _.map(WEEK_DAYS, (day) => ({day, windows: _.sortBy(windowsByDay[day] || [], 'from')}))
          : []
      };
    });
  }

  /**
   * Grouped sections (e.g. Skills) validate into account + program groups. The table works on file
   * rows, so each group entry becomes a row carrying its group's target.
   */
  private _flattenGroups(response: any[]): ProgramImportRow[] {
    const groupRowsKey = this.importType.groupRowsKey;

    if (!groupRowsKey) {
      return response;
    }

    const unitRowsKey = this.importType.unitRowsKey;

    if (unitRowsKey) {
      // Each row gets its unit's values, so a unit with an error (values null) flags all its rows.
      return _.flatMap(response, (group) => _.flatMap(group[groupRowsKey] || [], (unit) => {
        const unitId = this.UUIDService.generate();

        return _.map(unit[unitRowsKey] || [], (row) => ({
          ...row,
          accountId: group.accountId,
          programType: group.programType,
          values: unit.values,
          unitId
        }));
      }));
    }

    return _.flatMap(response, (group) => {
      const unitId = this.importType.removeGroupAsWhole ? this.UUIDService.generate() : undefined;

      return _.map(group[groupRowsKey] || [], (row) => ({
        ...row,
        accountId: group.accountId,
        programType: group.programType,
        unitId
      }));
    });
  }

  /**
   * Import expects the validate shape back: flat rows, or for grouped sections one group per
   * account + program in order of first appearance. Only the server-used fields are sent.
   */
  private _toImportPayload(rows: ProgramImportRow[]): any[] {
    const groupRowsKey = this.importType.groupRowsKey;

    if (!groupRowsKey) {
      return _.map(rows, (row) => _.omit(row, ['rowId', 'errorsDisplay', 'hasErrors', 'hasWarnings', 'cells']));
    }

    return _.chain(rows)
      .groupBy((row) => `${row.accountId}|${row.programType}`)
      .values()
      .map((groupRows: ProgramImportRow[]) => ({
        accountId: _.head(groupRows).accountId,
        programType: _.head(groupRows).programType,
        [groupRowsKey]: this.importType.unitRowsKey
          ? this._toUnitsPayload(groupRows)
          : _.map(groupRows, (row) => _.pick(row, ['rowNumber', 'values']))
      }))
      .value();
  }

  private _toUnitsPayload(rows: ProgramImportRow[]): any[] {
    return _.chain(rows)
      .groupBy('unitId')
      .values()
      .map((unitRows: ProgramImportRow[]) => ({
        values: _.head(unitRows).values,
        [this.importType.unitRowsKey]: _.map(unitRows, (row) => _.pick(row, ['rowNumber']))
      }))
      .value();
  }

  private _enrichRow(row: ProgramImportRow): ProgramImportRow {
    const mappingErrors = row.mappingErrors || [];
    const hasErrors = row.values === null || _.some(mappingErrors, (error) => this._isBlockingError(error));

    return {
      ...row,
      rowId: this.UUIDService.generate(),
      // The server nulls `values` exactly when a row has a blocking error, and would skip it silently.
      hasErrors,
      // Warning rows are imported, but some of them clear settings (e.g. empty Enabled + Surcharge).
      hasWarnings: !hasErrors && !_.isEmpty(mappingErrors),
      errorsDisplay: this._formatErrors(mappingErrors),
      cells: _.map(this.importType.columns, (column) => this._formatCell(this._getCellValue(row, column), column))
    };
  }

  private _getCellValue(row: ProgramImportRow, column: ProgramImportColumn): any {
    const resolved = column.valuesField ? _.get(row.values, column.valuesField) : undefined;
    return _.isNil(resolved) ? _.get(row.rawRow, column.field) : resolved;
  }

  private _formatCell(value: any, column: ProgramImportColumn): string {
    if (_.isNil(value) || value === '') {
      return '-';
    }

    if (column.format === 'time') {
      return this._formatTime(value);
    }

    return _.isArray(value) ? value.join(', ') : String(value);
  }

  /**
   * Times come as decimal hours in `values` (8.5 = 08:30, 24 = end of day) and, for rows with
   * errors, as whatever the server's LocalTime serialization gives in `rawRow` ("08:30:00" or
   * [8, 30]).
   */
  private _formatTime(value: any): string {
    const pad = (n: number) => _.padStart(String(n), 2, '0');

    if (_.isNumber(value)) {
      let hours = Math.floor(value);
      let minutes = Math.round((value - hours) * 60);
      if (minutes === 60) {
        hours += 1;
        minutes = 0;
      }
      return `${pad(hours)}:${pad(minutes)}`;
    }

    if (_.isArray(value)) {
      return `${pad(value[0] || 0)}:${pad(value[1] || 0)}`;
    }

    return _.isString(value) ? value.slice(0, 5) : String(value);
  }

  private _formatErrors(mappingErrors: Array<ProgramImportMappingError | string>): string {
    const messages = _.chain(mappingErrors)
      .map((error) => _.isString(error) ? error : (error?.description || error?.message || error?.fieldName))
      .compact()
      .uniq()
      .value();

    return messages.length ? messages.join(', ') : '-';
  }

  private _isBlockingError(error: ProgramImportMappingError | string): boolean {
    return _.isString(error) || !error.type || error.type === 'Error';
  }

  private _getTotals(rows: ProgramImportRow[]) {
    return {
      total: rows.length,
      errors: _.filter(rows, (row) => row.hasErrors).length
    };
  }

  private _applyBatchErrors(errorItems: any[]) {
    if (!errorItems.length) {
      this.pricePlanAlertsService.importProgramSettingsSuccess();
      this._reset();
      this.onBackToPrograms();
      return;
    }

    // All rows stay in the table: the import is all-or-nothing, so the user fixes the flagged
    // rows and re-uploads the whole file rather than re-sending only the failed ones.
    const matchedRowNumbers = [];
    const rows = _.map(this.rows, (row) => {
      const errorItem = _.find(errorItems, (item) => item.rowNumber === row.rowNumber);

      if (!errorItem) {
        return row;
      }

      matchedRowNumbers.push(row.rowNumber);
      return this._enrichRow({...row, mappingErrors: _.get(errorItem, 'mappingErrors', [])});
    });

    // Price plan import tasks carry up to 100 rows and no row number, so a failure usually
    // cannot be pinned to a row - surface it instead of showing an empty errors tab.
    if (!matchedRowNumbers.length) {
      this.pricePlanAlertsService.importProgramSettingsError();
      return;
    }

    this.currentNavItem = 'errors';
    this._applyRows(rows);
  }

  private _reset() {
    this.currentNavItem = 'total';
    this._applyRows([]);
    this.confirmChangeStateService.reset();
  }
}

angular
  .module('bringoz.admin.pricePlan')
  .component('programImportView', {
    bindings: {
      importTypeId: '<'
    },
    template: require('./program-import-view.html').default,
    controller: ProgramImportViewCtrl
  });
